import { Producer } from 'types/producer'
import { AllProducts } from 'types/product-data'
import {
  COUNTER_TYPES,
  CreateCustomerRequestBody,
  ELProduct,
  METER_TYPES,
  NGProduct,
  OPT_IN_POINTS,
  REGIONS,
  SETTLEMENT_FREQUENCIES,
  SIGN_CHANNELS,
  SIMULATION_TYPES,
  USAGE_GRADES
} from '@boltenergy-be/shared'
import { store } from 'store/index'
import { InputGroups } from 'store/customer/types'
import { getSimulatedPrice } from 'utils/price'
import { getElectricityProduct, getGasProduct } from 'utils/customerFlow'
import {
  fileToBase64,
  getPricelistFromElectricityProducts,
  getPricelistFromGasProducts,
  removeNonAlphanumericals,
  removeNonNumbers
} from 'utils/helpers'
import { getUpperCaseLanguage } from 'utils/format'
import { MeterDetailsFieldKeys, SimulationFieldKeys } from 'store/customer/enums'
import {
  checkCanPassIndexesAndMeterNumbers,
  getFixedPricesForApiRequests,
  getPreviousInhabitant,
  getSharedMarketingDataForRequestBody
} from './registrations.utils'
import dayjs from 'dayjs'
import { determineSimulationUsage } from 'features/simulation/utils'
import { formatPhoneToE164 } from '@boltenergy-be/design-system/utils'

/**
 * Return number if value is given or 0 as fallback
 * @param {number | string | null | undefined} index
 * @returns {number}
 */
export const formatIndex = (index: number | string | null | undefined): number => {
  if (typeof index !== 'number' && typeof index !== 'string') {
    return 0
  }

  if (typeof index === 'string' && index.match(/[^0-9.]+/)) {
    throw new Error('Index should not contain letters.')
  }

  const numberIndex = Number(index)

  return isNaN(numberIndex) ? 0 : numberIndex
}

/**
 * Returns the registration & simulation data as a CreateCustomer object
 *
 * @param {AllProducts} allProducts
 * @param {Producer} producer
 * @returns {Promise<CreateCustomerRequestBody>}
 */
export const parseCreateCustomerData = async (producer: Producer, allProducts: AllProducts): Promise<CreateCustomerRequestBody> => {
  // Fetch data from the redux store
  const { opportunityId } = store.getState().app
  const { fixedPrices, inputs } = store.getState().customer
  const marketing = store.getState().marketing
  const { simulation, deliveryAddress, personalData, meterDetails, billing, overview } = inputs as InputGroups

  // Define the price object
  const price = getSimulatedPrice()

  // Define the products
  const electricityProduct = getElectricityProduct(allProducts)
  const gasProduct = getGasProduct(allProducts)

  // Check if indexes should be given
  const shouldPassIndexesAndMeterNumbers = checkCanPassIndexesAndMeterNumbers(
    meterDetails.situation,
    meterDetails.counterType,
    meterDetails.contractStartDate
  )

  if (personalData.isCompany && !personalData.companyNumber) {
    throw new Error('Company number is required for company accounts.')
  }

  return {
    // NEW
    account: {
      birthDate: personalData?.dateOfBirth || undefined,
      company: personalData.isCompany
        ? {
            name: personalData.companyName,
            enterpriseNumber: personalData.companyNumber ? removeNonNumbers(personalData.companyNumber) : undefined,
            legalForm: personalData.legalForm,
            vatApplication: personalData.subjectToVat || false
          }
        : undefined,
      correspondenceAddress: {
        ...(billing.sameCorrespondenceAddress ? deliveryAddress.address : billing.correspondenceAddress),
        postalCode: Number(
          billing.sameCorrespondenceAddress ? deliveryAddress.address.postalCode : billing.correspondenceAddress.postalCode
        )
      },
      paymentDetails: {
        ibanNumber: billing.iban ? removeNonAlphanumericals(billing.iban) : undefined,
        paymentMethod: billing.paymentMethod
      }
    },
    contact: {
      contactMode: billing.deliveryMode,
      email: personalData.email?.trim(),
      firstName: personalData.firstName?.trim(),
      language: getUpperCaseLanguage(personalData.language),
      lastName: personalData.lastName?.trim(),
      phoneMobile: formatPhoneToE164(personalData.mobilePhone)
    },
    contract: {
      deliveryAddress: {
        ...deliveryAddress.address,
        postalCode: Number(deliveryAddress.address.postalCode)
      },
      dynamicTariff:
        meterDetails[MeterDetailsFieldKeys.COUNTER_TYPE] === COUNTER_TYPES.digital &&
        simulation[SimulationFieldKeys.REGION] === REGIONS.flanders
          ? meterDetails.dynamicTariff
          : false,
      prices:
        inputs.simulation.chosenSimulationType === SIMULATION_TYPES.eav
          ? {}
          : {
              fixed: getFixedPricesForApiRequests(fixedPrices, inputs.simulation.chosenSimulationType),
              forcedAmount: simulation.forcedAmount || undefined,
              simulation: price
            },
      priceListUrl: marketing.pricelistUrls ?? {
        electricity: getPricelistFromElectricityProducts(
          electricityProduct.details.code as ELProduct,
          allProducts,
          personalData.isCompany,
          personalData.language
        ),
        gas: getPricelistFromGasProducts(gasProduct.details.code as NGProduct, allProducts, personalData.isCompany, personalData.language)
      },
      producerId: producer.id,
      products: {
        electricity: electricityProduct.details.code as ELProduct,
        gas: gasProduct.details.code as NGProduct
      },
      promoCodeSfId: simulation?.promoCode?.promoCodeId,
      referrerSfContactId: simulation?.referralCode?.referrer?.id,
      settlementFrequency: meterDetails.settlementFrequency || SETTLEMENT_FREQUENCIES.yearly,
      situation: meterDetails.situation,
      startDate: dayjs(meterDetails.contractStartDate).format('YYYY-MM-DD'),
      uploadDataUrls: {
        energyTransferDocument: meterDetails?.upload?.eod?.[0] ? ((await fileToBase64(meterDetails.upload.eod[0])) as string) : undefined,
        contract: meterDetails?.upload?.contract?.[0] ? ((await fileToBase64(meterDetails.upload.contract[0])) as string) : undefined,
        signature: overview.chosenSignChannel === SIGN_CHANNELS.signature ? overview.signature : undefined
      },
      usage:
        inputs.simulation.chosenSimulationType === SIMULATION_TYPES.express ||
        inputs.simulation.chosenSimulationType === SIMULATION_TYPES.pricingSimulation
          ? simulation.usage
          : determineSimulationUsage(USAGE_GRADES.low)
    },
    marketing: {
      ...getSharedMarketingDataForRequestBody(
        marketing,
        inputs.simulation.chosenSimulationType,
        overview.chosenSignChannel || SIGN_CHANNELS.none
      ),
      eanCaptureType: meterDetails.eanCaptureType,
      noEanFlow: marketing.noEanFlow,
      signChannel: overview.chosenSignChannel || SIGN_CHANNELS.none,
      optinPoint: OPT_IN_POINTS.websiteRegistration
    },
    meter: {
      counterType: meterDetails.counterType,
      meterType: simulation.meterType,
      electricity: meterDetails.knowsMeterDetails
        ? {
            ean: meterDetails.electricity.ean?.toString() || undefined,
            exclusiveNightEan:
              shouldPassIndexesAndMeterNumbers && simulation[SimulationFieldKeys.HAS_EXCLUSIVE_NIGHT_METER]
                ? meterDetails.electricity.eanExclNight?.toString() || undefined
                : undefined,
            indexes: shouldPassIndexesAndMeterNumbers
              ? {
                  single: simulation.meterType === METER_TYPES.single ? formatIndex(meterDetails.electricity.total) : undefined,
                  double:
                    simulation.meterType === METER_TYPES.double
                      ? {
                          day: formatIndex(meterDetails.electricity.day),
                          night: formatIndex(meterDetails.electricity.night)
                        }
                      : undefined,
                  exclusiveNight: simulation[SimulationFieldKeys.HAS_EXCLUSIVE_NIGHT_METER]
                    ? formatIndex(meterDetails.electricity.exclNight)
                    : undefined
                }
              : undefined,
            meterNumber: shouldPassIndexesAndMeterNumbers ? meterDetails.electricity.meterNumber?.toString() || undefined : undefined
          }
        : {},
      gas:
        meterDetails.knowsMeterDetails && deliveryAddress.needsGas
          ? {
              ean: meterDetails.gas.ean?.toString() || undefined,
              meterNumber: shouldPassIndexesAndMeterNumbers ? meterDetails.gas.meterNumber?.toString() || undefined : undefined,
              index: shouldPassIndexesAndMeterNumbers ? formatIndex(meterDetails.gas.total) : undefined
            }
          : undefined,
      ...(meterDetails.meterReadingMonth && {
        readingMonth: Number(meterDetails.meterReadingMonth)
      })
    },
    opportunityId: opportunityId || undefined,
    previousSupplier: meterDetails?.[MeterDetailsFieldKeys.PREVIOUS_SUPPLIER]?.supplier || undefined,
    previousInhabitant: getPreviousInhabitant(meterDetails.previousInhabitant),
    solarPanels: meterDetails.hasSolarPanels,
    walloniaSolarPanelsSince2024:
      simulation[SimulationFieldKeys.REGION] === REGIONS.wallonia && meterDetails.counterType === COUNTER_TYPES.digital
        ? meterDetails.walloniaSolarPanelsSince2024
        : undefined
  } satisfies CreateCustomerRequestBody
}
